/**
 * 의도 + 후보 + 지금 포커스 → 행동 하나 (v10, `docs/25` §3.2).
 *
 * ── `preventDefault` 를 반환값에 넣는 이유 (절대 규칙 31) ──────
 *
 * 어댑터가 스스로 `event.preventDefault()` 를 부르면 **「언제 기본 동작을 막는가」가 순수
 * 함수 밖으로 나간다.** 그 판정이야말로 조용히 틀리는 자리다 — 과하게 막으면 문서형
 * 화면(`/install`·`/done`·상품)이 키보드로 스크롤되지 않고, 덜 막으면 화살표가 `<video>`
 * 에서 seek 을 일으킨다. 둘 다 「리모컨을 만들었더니 다른 게 깨졌다」로 나타난다.
 *
 * 그래서 어댑터가 하는 일은 **이 함수가 낸 값을 그대로 실행하는 것**뿐이다.
 */

import { entryFocusIndex, nextFocusIndex } from './geometry.js';
import type { FocusCandidate } from './geometry.js';
import type { NavIntent } from './keys.js';

export type NavAction =
  /** 이 후보로 포커스를 옮긴다 */
  | { kind: 'focus'; index: number; preventDefault: true }
  /** 이 후보를 누른다. **뒤로 키만 쓴다** — Enter 는 네이티브가 한다 (절대 규칙 30) */
  | { kind: 'activate'; index: number; preventDefault: true }
  /** 아무것도 하지 않는다. `preventDefault` 가 거짓이면 **브라우저에게 넘긴다** */
  | { kind: 'none'; preventDefault: boolean };

export interface NavContext {
  readonly intent: NavIntent;
  /** 지금 층의 후보 전부. DOM 순서로 `index` 가 매겨져 있다 */
  readonly candidates: readonly FocusCandidate[];
  /** 지금 포커스된 후보의 `index`. **층 밖이면 `null`** */
  readonly activeIndex: number | null;
  /** 이 층의 닫기 버튼(`data-focus-back`). 없으면 `null` */
  readonly backIndex: number | null;
}

/** 브라우저에게 넘긴다. **막지 않는 것이 기본이다.** */
const PASS: NavAction = { kind: 'none', preventDefault: false };

/**
 * 이 키가 무엇을 해야 하는가.
 *
 * ── 층 밖 포커스를 먼저 본다 (절대 규칙 32) ───────────────────
 *
 * 시트가 열려도 `document.activeElement` 는 **시트를 연 버튼에 그대로 남아 있다.** 화살표만
 * 다루면 시트 위에서 Enter 를 눌렀을 때 뒤의 `구매하기` 가 다시 눌린다 — 규칙 14 위반이고
 * 증상이 조용하다. **그 한 분기가 넷을 함께 처리한다:** ① 시트가 열린 직후 ② 시트가 닫혀
 * 포커스한 버튼이 언마운트된 직후 ③ 상품이 바뀌어 오버레이가 닫힌 직후 ④ 채널 전환으로
 * 트리가 새로 선 직후. **상태가 하나도 필요 없는 이유가 이것이다.**
 */
export function decideNavAction(context: NavContext): NavAction {
  const { intent, candidates, activeIndex, backIndex } = context;

  // 후보가 없다 (`/debug` 처럼 클릭 가능 요소가 0개인 화면). **던지지 않는다** —
  // 던지면 그 화면에서 리모컨이 죽고, 뒤로 키로 빠져나갈 수조차 없다.
  if (candidates.length === 0) return PASS;

  if (intent.kind === 'back') {
    // 닫을 층이 없으면 넘긴다 — 브라우저·TV 의 기본 뒤로가기를 삼키지 않는다.
    return backIndex === null ? PASS : { kind: 'activate', index: backIndex, preventDefault: true };
  }

  const active = activeIndex === null ? undefined : candidates.find((c) => c.index === activeIndex);

  if (active === undefined) {
    const entry = entryFocusIndex(candidates);
    return entry === null ? PASS : { kind: 'focus', index: entry, preventDefault: true };
  }

  // **Enter 는 여기서 끝난다.** 층 안에 포커스가 있으면 네이티브 `<button>` 이 누른다 —
  // 우리가 막으면 Enter 가 죽고, 우리가 누르면 두 번 눌린다 (절대 규칙 30).
  if (intent.kind === 'confirm') return PASS;

  const next = nextFocusIndex(active, candidates, intent.direction);

  // 가장자리다. **막지 않는 것이 여기의 요점이다** — 문서형 화면의 네이티브 스크롤이
  // 이 한 줄에 걸려 있다 (`docs/25` D6).
  return next === null ? PASS : { kind: 'focus', index: next, preventDefault: true };
}
